import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import swal from "sweetalert";
import {
  FiPlus,
  FiSearch,
  FiX,
  FiRotateCcw,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiCopy,
  FiShield,
  FiMail,
  FiCheckCircle,
  FiCode,
} from "react-icons/fi";
import { HiOutlineDotsVertical } from "react-icons/hi";

import Layout from "../../components/global/layout";
import loader from "../../methods/loader";
import CommonPagination from "../../components/common/CommonPagination";
import FormControl from "../../components/common/FormControl";
import {
  MODULE_CONFIG,
  STANDARD_EMAIL_TYPES,
  EMAIL_TYPE_MAP,
  EmailTemplateService,
} from "./shared";
import { hasPermission } from "../../utils/permission";

// Reusable Action Dropdown Menu
const ActionMenu = ({ actions = [], align = "left" }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".action-menu-container")) {
        setIsOpen(false);
      }
    };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, [isOpen]);

  const visibleActions = actions.filter(Boolean);
  if (!visibleActions.length) return null;

  return (
    <div className="relative inline-block text-left action-menu-container">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded-lg text-slate-700 hover:text-[#115B9D] hover:bg-slate-100 transition-colors cursor-pointer"
        title="Open Actions"
      >
        <HiOutlineDotsVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          className={`absolute ${
            align === "right" ? "right-0" : "left-0"
          } mt-1 w-44 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-left`}
        >
          {visibleActions.map((action, idx) => (
            <button
              key={`act-${idx}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                action.onClick?.();
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer text-left ${
                action.isDanger
                  ? "text-rose-700 hover:bg-rose-50 hover:text-rose-900"
                  : "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
              }`}
            >
              {action.icon}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function EmailTemplatesList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  // RBAC permissions
  const canCreate = hasPermission(user, "email_templates", "create");
  const canEdit = hasPermission(user, "email_templates", "edit");
  const canDelete = hasPermission(user, "email_templates", "delete");

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters & Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (!val || !val.trim()) {
      loader(true);
      setDebouncedSearch("");
      setPage(1);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      loader(true);
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch Templates
  const fetchTemplates = useCallback(
    async (showMainLoader = false) => {
      if (showMainLoader) loader(true);
      setLoading(true);

      try {
        const params = {
          page,
          limit,
        };
        if (debouncedSearch) params.search = debouncedSearch;
        if (typeFilter !== "all") params.email_type = typeFilter;

        const res = await EmailTemplateService.list(params);

        if (res?.success) {
          let records =
            res?.data?.templates ||
            res?.data?.data ||
            res?.data ||
            res?.templates ||
            res?.items ||
            [];

          if (!Array.isArray(records) && typeof records === "object") {
            records = Object.values(records);
          }

          // Apply client-side filter for type if backend didn't filter
          if (typeFilter !== "all") {
            records = records.filter((r) => r.email_type === typeFilter);
          }

          // Apply client search filter if needed
          if (debouncedSearch) {
            const q = debouncedSearch.toLowerCase();
            records = records.filter(
              (r) =>
                r.name?.toLowerCase().includes(q) ||
                r.subject?.toLowerCase().includes(q) ||
                r.email_type?.toLowerCase().includes(q)
            );
          }

          setTotalCount(res?.totalCount ?? res?.total ?? records.length);
          setItems(records);
        } else {
          setItems([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error("Failed to load email templates:", err);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        loader(false);
      }
    },
    [page, limit, typeFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchTemplates(true);
  }, [fetchTemplates]);

  // Reset Filters
  const handleResetFilters = () => {
    loader(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setTypeFilter("all");
    setPage(1);
  };

  // Delete Template with Protection for Default Backups
  const handleDelete = async (template) => {
    const id = template?._id || template?.id;
    const templateName = template?.name || "Email Template";

    if (template?.is_default_backup) {
      swal({
        title: "Cannot Delete Default Backup",
        text: `"${templateName}" is marked as a Global Default Backup for ${template?.email_type}. Default backup templates cannot be deleted because they prevent email dispatch failures. You must designate another template as the backup first.`,
        icon: "warning",
        buttons: ["Understood", false],
        dangerMode: true,
      });
      return;
    }

    const confirmed = await swal({
      title: "Delete Email Template?",
      text: `Are you sure you want to delete "${templateName}"? This action will remove the master template from the system.`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete Template"],
      dangerMode: true,
    });

    if (!confirmed) return;

    loader(true);
    try {
      const res = await EmailTemplateService.delete(id);
      if (res?.success) {
        toast.success("Email template deleted successfully.");
        fetchTemplates(false);
      } else {
        toast.error(res?.message || "Failed to delete email template.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("An error occurred while deleting the template.");
    } finally {
      loader(false);
    }
  };

  const isFilterActive = Boolean(
    searchQuery.trim() || typeFilter !== "all"
  );

  // Type options for FormControl select
  const typeOptions = [
    { id: "all", name: "All Email Types" },
    ...STANDARD_EMAIL_TYPES.map((t) => ({ id: t.id, name: t.name })),
  ];

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & FILTER BAR (SPACIOUS 2-TIER DESIGN)       */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-4">
          {/* Top Tier: Title, Subtitle & Primary Action */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
            <div className="space-y-1">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                {MODULE_CONFIG.title}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 font-medium max-w-3xl leading-relaxed">
                {MODULE_CONFIG.subtitle}
              </p>
            </div>

            {/* Create Template Action (Protected by RBAC) */}
            {canCreate && (
              <button
                type="button"
                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/create`)}
                className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 h-10 sm:h-11 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs sm:text-sm font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer whitespace-nowrap active:scale-[0.98] shrink-0 self-start sm:self-auto"
              >
                <FiPlus className="w-4 h-4" />
                <span>Create Master Template</span>
              </button>
            )}
          </div>

          {/* Bottom Tier: Search & Filter Toolbar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
              {/* Search Input */}
              <div className="w-full sm:w-80 md:w-96">
                <div className="relative flex items-center group">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-[#115B9D] transition-colors">
                    <FiSearch className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search by name, subject, or email type..."
                    className="w-full bg-white border-[1.5px] border-slate-500 rounded-xl pl-10 pr-9 h-10 text-xs sm:text-sm font-semibold text-slate-900 placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 focus:border-slate-500 shadow-box"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => handleSearchChange("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                      title="Clear search"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Email Type Filter */}
              <div className="w-full sm:w-60 shrink-0">
                <FormControl
                  type="select"
                  placeholder="Filter by Email Type"
                  value={typeFilter}
                  theme="search"
                  options={typeOptions}
                  onChange={(val) => {
                    loader(true);
                    setTypeFilter(val || "all");
                    setPage(1);
                  }}
                />
              </div>

              {/* Reset Filters Button */}
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={!isFilterActive}
                className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  isFilterActive
                    ? "bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-800 hover:text-slate-950 border border-slate-300 hover:border-slate-400 shadow-xs"
                    : "bg-slate-50 text-slate-400 border border-slate-200 opacity-50 cursor-not-allowed"
                }`}
                title="Reset all search and status filters"
              >
                <FiRotateCcw className={`w-3.5 h-3.5 ${isFilterActive ? "text-slate-700" : "text-slate-400"}`} />
                <span>Reset</span>
              </button>
            </div>

            {/* Total Records Counter */}
            {typeof totalCount === "number" && (
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-2 self-center">
                <span>Total:</span>
                <span className="font-bold text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                  {totalCount} {totalCount === 1 ? "Template" : "Templates"}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. EMAIL TEMPLATES LISTING TABLE                          */}
        {/* ======================================================== */}
        <div className="bg-transparent md:bg-white md:rounded-2xl md:border md:border-slate-300 md:shadow-[0_2px_10px_rgba(0,0,0,0.03)] overflow-visible">
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <div className="inline-block w-8 h-8 border-3 border-slate-200 border-t-[#115B9D] rounded-full animate-spin" />
              <p className="text-xs font-semibold text-slate-500">Loading master email templates...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-4 px-4">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 border border-slate-300 flex items-center justify-center">
                <FiMail className="w-7 h-7" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-bold text-slate-900">No Email Templates Found</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  {isFilterActive
                    ? "No templates matched your filter or search criteria. Try resetting filters."
                    : "Get started by creating your first master transactional email template."}
                </p>
              </div>
              {isFilterActive ? (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-800 hover:bg-slate-200 transition-colors cursor-pointer"
                >
                  <FiRotateCcw className="w-3.5 h-3.5" />
                  <span>Clear All Filters</span>
                </button>
              ) : (
                canCreate && (
                  <button
                    type="button"
                    onClick={() => navigate(`${MODULE_CONFIG.routeBase}/create`)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-[#115B9D] text-white hover:bg-[#0e4b82] transition-colors shadow-sm cursor-pointer"
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    <span>Create Master Template</span>
                  </button>
                )
              )}
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW WITH FIXED STICKY HEADER */}
              <div className="hidden md:block w-full">
                <table className="w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col style={{ width: "6%" }} />   {/* 1. Action */}
                    <col style={{ width: "32%" }} />  {/* 2. Template Name */}
                    <col style={{ width: "18%" }} />  {/* 3. Type */}
                    <col style={{ width: "30%" }} />  {/* 4. Subject Line */}
                    <col style={{ width: "14%" }} />  {/* 5. Modified Date */}
                  </colgroup>

                  <thead className="sticky top-[72px] z-30 bg-slate-100 shadow-xs">
                    <tr className="border-b border-slate-300 text-[11px] font-black uppercase tracking-wider text-slate-800 bg-slate-100">
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-3 text-center whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tl-2xl">
                        Action
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Template Name
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Type
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Subject Line
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tr-2xl">
                        Modified Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200">
                    {items.map((item, index) => {
                      const id = item?.id || item?._id;
                      const name = item?.name || "Untitled Template";
                      const emailType = item?.email_type || "CUSTOM";
                      const typeConfig = EMAIL_TYPE_MAP[emailType] || {
                        name: emailType,
                        badgeClass: "bg-slate-100 text-slate-800 border-slate-300",
                      };
                      const subject = item?.subject || "—";
                      const updatedAt = item?.updatedAt || item?.createdAt;

                      return (
                        <tr
                          key={id || `tmpl-${index}`}
                          className="hover:bg-slate-50/80 transition-colors"
                        >
                          {/* 1. Action Dropdown Menu */}
                          <td className="py-3.5 px-3 text-center">
                            <ActionMenu
                              actions={[
                                {
                                  label: "View Dossier",
                                  icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                  onClick: () =>
                                    navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                                },
                                canEdit
                                  ? {
                                      label: "Edit Template",
                                      icon: <FiEdit3 className="w-4 h-4 text-slate-800" />,
                                      onClick: () =>
                                        navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`),
                                    }
                                  : null,
                                canCreate
                                  ? {
                                      label: "Clone Template",
                                      icon: <FiCopy className="w-4 h-4 text-slate-800" />,
                                      onClick: () =>
                                        navigate(`${MODULE_CONFIG.routeBase}/create?clone=${id}`),
                                    }
                                  : null,
                                canDelete
                                  ? {
                                      label: "Delete Template",
                                      icon: <FiTrash2 className="w-4 h-4 text-rose-700" />,
                                      onClick: () => handleDelete(item),
                                      isDanger: true,
                                    }
                                  : null,
                              ].filter(Boolean)}
                            />
                          </td>

                          {/* 2. Template Name */}
                          <td className="py-3.5 px-4">
                            <div
                              onClick={() =>
                                navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)
                              }
                              className="font-bold text-xs sm:text-sm text-slate-900 hover:text-[#115B9D] cursor-pointer truncate transition-colors"
                              title={name}
                            >
                              {name}
                            </div>
                          </td>

                          {/* 3. Type (Dedicated Column) */}
                          <td className="py-3.5 px-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${typeConfig.badgeClass}`}
                            >
                              {typeConfig.name}
                            </span>
                          </td>

                          {/* 4. Subject Line */}
                          <td className="py-3.5 px-4">
                            <div
                              className="font-medium text-xs text-slate-700 truncate"
                              title={subject}
                            >
                              {subject}
                            </div>
                          </td>

                          {/* 5. Modified Date */}
                          <td className="py-3.5 px-4 text-left whitespace-nowrap">
                            <span className="text-xs font-semibold text-slate-600">
                              {updatedAt ? new Date(updatedAt).toLocaleDateString() : "—"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS VIEW */}
              <div className="block md:hidden space-y-3 p-3">
                {items.map((item, index) => {
                  const id = item?.id || item?._id;
                  const name = item?.name || "Untitled Template";
                  const emailType = item?.email_type || "CUSTOM";
                  const typeConfig = EMAIL_TYPE_MAP[emailType] || {
                    name: emailType,
                    badgeClass: "bg-slate-100 text-slate-800 border-slate-300",
                  };
                  const subject = item?.subject || "—";

                  return (
                    <div
                      key={id || `tmpl-card-${index}`}
                      className="bg-white rounded-2xl p-4 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-3"
                    >
                      <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                        <div className="min-w-0">
                          <h3
                            onClick={() =>
                              navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)
                            }
                            className="font-bold text-xs sm:text-sm text-slate-900 truncate hover:text-[#115B9D] cursor-pointer"
                          >
                            {name}
                          </h3>
                          <div className="mt-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${typeConfig.badgeClass}`}
                            >
                              {typeConfig.name}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <ActionMenu
                            align="right"
                            actions={[
                              {
                                label: "View Dossier",
                                icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                onClick: () =>
                                  navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                              },
                              canEdit
                                ? {
                                    label: "Edit Template",
                                    icon: <FiEdit3 className="w-4 h-4 text-slate-800" />,
                                    onClick: () =>
                                      navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`),
                                  }
                                : null,
                              canDelete
                                ? {
                                    label: "Delete Template",
                                    icon: <FiTrash2 className="w-4 h-4 text-rose-700" />,
                                    onClick: () => handleDelete(item),
                                    isDanger: true,
                                  }
                                : null,
                            ].filter(Boolean)}
                          />
                        </div>
                      </div>

                      <div className="text-xs">
                        <span className="font-semibold text-slate-500">Subject: </span>
                        <span className="font-medium text-slate-800">{subject}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION CONTROLS */}
              {totalCount > limit && (
                <div className="p-4 bg-white border-t border-slate-200 rounded-b-2xl">
                  <CommonPagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={(p) => {
                      loader(true);
                      setPage(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onLimitChange={(l) => {
                      loader(true);
                      setLimit(l);
                      setPage(1);
                    }}
                    limitOptions={[25, 50, 100]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
